import React, { useState } from "react";
import { logUserBugReport } from "@/lib/errorLogger";
import { Bug, X, Send, CheckCircle2 } from "lucide-react";

/**
 * Floating button "Zglos blad/sugestie" — widoczny zawsze, dolnym prawym rogu.
 * Klik otwiera prosty modal. Wysylka -> /api/monitor/user-report.
 */
export default function BugReportWidget() {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [email, setEmail] = useState("");
  const [severity, setSeverity] = useState("medium");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const reset = () => {
    setTitle("");
    setDescription("");
    setSeverity("medium");
    setEmail("");
    setSent(false);
    setError("");
    setSending(false);
  };

  const close = () => {
    setOpen(false);
    setTimeout(reset, 300);
  };

  const submit = async () => {
    if (!description.trim()) {
      setError("Opisz proszę co się stało (min. kilka słów).");
      return;
    }
    setSending(true);
    setError("");
    try {
      const res = await logUserBugReport({ title, description, severity, user_email: email });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setSent(true);
      setTimeout(close, 2000);
    } catch (e) {
      setError("Nie udało się wysłać: " + (e?.message || "spróbuj ponownie"));
      setSending(false);
    }
  };

  return (
    <>
      {!open && (
        <button
          onClick={() => setOpen(true)}
          aria-label="Zgłoś błąd lub sugestię"
          style={{
            position: "fixed",
            right: 16,
            bottom: 16,
            zIndex: 9999,
            width: 48,
            height: 48,
            borderRadius: "50%",
            background: "#ef4444",
            color: "white",
            border: "none",
            boxShadow: "0 4px 12px rgba(0,0,0,0.18)",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Bug size={22} />
        </button>
      )}

      {open && (
        <div
          style={{
            position: "fixed",
            right: 16,
            bottom: 16,
            zIndex: 9999,
            width: 340,
            maxWidth: "calc(100vw - 32px)",
            background: "white",
            borderRadius: 12,
            boxShadow: "0 8px 30px rgba(0,0,0,0.25)",
            border: "1px solid #e5e7eb",
            overflow: "hidden",
            fontFamily: "system-ui, -apple-system, sans-serif",
          }}
        >
          <div style={{ background: "#ef4444", color: "white", padding: "12px 14px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 600 }}>
              <Bug size={18} /> Zgłoś problem
            </div>
            <button onClick={close} style={{ background: "transparent", border: 0, color: "white", cursor: "pointer" }} aria-label="Zamknij">
              <X size={18} />
            </button>
          </div>

          <div style={{ padding: 14 }}>
            {sent ? (
              <div style={{ textAlign: "center", padding: "20px 0" }}>
                <CheckCircle2 size={42} color="#16a34a" />
                <p style={{ marginTop: 12, fontSize: 14, color: "#16a34a", fontWeight: 600 }}>
                  Dzięki! Zgłoszenie odebrane.
                </p>
              </div>
            ) : (
              <>
                <label style={{ fontSize: 12, color: "#6b7280" }}>Tytuł (opcjonalny)</label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="np. Klikam Dashboard i biały ekran"
                  style={{ width: "100%", marginTop: 4, padding: "6px 8px", border: "1px solid #d1d5db", borderRadius: 6, fontSize: 13, boxSizing: "border-box" }}
                />

                <label style={{ fontSize: 12, color: "#6b7280", marginTop: 10, display: "block" }}>Co się stało? *</label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Opisz problem, co kliknales, co sie wydarzylo"
                  rows={4}
                  style={{ width: "100%", marginTop: 4, padding: "6px 8px", border: "1px solid #d1d5db", borderRadius: 6, fontSize: 13, boxSizing: "border-box", resize: "vertical" }}
                />

                <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                  <div style={{ flex: 1 }}>
                    <label style={{ fontSize: 12, color: "#6b7280" }}>Ważność</label>
                    <select
                      value={severity}
                      onChange={(e) => setSeverity(e.target.value)}
                      style={{ width: "100%", marginTop: 4, padding: "6px 8px", border: "1px solid #d1d5db", borderRadius: 6, fontSize: 13, boxSizing: "border-box" }}
                    >
                      <option value="low">Niska</option>
                      <option value="medium">Średnia</option>
                      <option value="high">Wysoka</option>
                      <option value="critical">Krytyczna</option>
                    </select>
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={{ fontSize: 12, color: "#6b7280" }}>Email (opcjonalny)</label>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="dla kontaktu"
                      style={{ width: "100%", marginTop: 4, padding: "6px 8px", border: "1px solid #d1d5db", borderRadius: 6, fontSize: 13, boxSizing: "border-box" }}
                    />
                  </div>
                </div>

                {error && <div style={{ marginTop: 10, padding: 8, background: "#fef2f2", color: "#dc2626", fontSize: 12, borderRadius: 6 }}>{error}</div>}

                <button
                  onClick={submit}
                  disabled={sending}
                  style={{ marginTop: 14, width: "100%", padding: 10, background: sending ? "#9ca3af" : "#ef4444", color: "white", border: 0, borderRadius: 6, cursor: sending ? "default" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 6, fontWeight: 600 }}
                >
                  <Send size={16} /> {sending ? "Wysyłam..." : "Wyślij"}
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}
